import { ReactNode, useState, useEffect, useRef } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Avatar } from '@/components/design-system'
import {
  Users, Cable, FileCode2, Fingerprint, Shield, Menu, Plug,
  PanelLeft, ChevronDown, Settings, LogOut,
  Sun, Moon, Monitor, LayoutDashboard, MessagesSquare, Workflow
} from 'lucide-react'
import FlashMessages from '@/frontend/components/FlashMessages'
import UndoToastHost from '@/frontend/components/undo/UndoToastHost'
import LavaOrb from '@/frontend/components/LavaOrb'
import OrganizationSwitcher from '@/frontend/components/OrganizationSwitcher'
import { useDropdown } from '@/frontend/hooks/useDropdown'
import type { SharedProps } from '@/frontend/types/inertia'
import { useTheme } from '@/frontend/contexts/ThemeContext'
import CommandPalette from '@/frontend/components/CommandPalette'
import { SidebarConversationsPanel } from '@/frontend/pages/Dashboard/AgentChats/components/SidebarConversationsPanel'
import WorkerFirstSidebar from '@/frontend/layouts/WorkerFirstSidebar'
import { useProvisioningPoll } from '@/frontend/hooks/useProvisioningPoll'
import { subscribeBusinessProcessEditChatVisibility } from '@/frontend/features/AgentChats/assistant-ui/businessProcessEditChatBus'
import AssistantFloatingWidgetHost from '@/frontend/features/AgentChats/assistant-ui/components/AssistantFloatingWidgetHost'
import LegacyForgeNavigation from '@/frontend/layouts/LegacyForgeNavigation'
import DashboardCatalogLink from '@/frontend/features/DashboardRuntime/CatalogLink'
import NavItem from '@/frontend/layouts/LegacyNavItem'
import { dashboardViewportClassName, dashboardContentOverflowClassName } from '@/frontend/layouts/dashboardViewport'

interface Props {
  children: ReactNode;
  variant?: 'default' | 'fullscreen' | 'canvas';
}

interface PageProps extends SharedProps {}

const SIDEBAR_COOKIE_PREFIX = 'stationzero_sidebar_expanded'

function sidebarCookieName(userId: string | number | null | undefined): string {
  return `${SIDEBAR_COOKIE_PREFIX}_${userId ?? 'anonymous'}`
}

function readSidebarCookie(userId: string | number | null | undefined): boolean {
  if (typeof document === 'undefined') return true

  const name = `${encodeURIComponent(sidebarCookieName(userId))}=`
  const value = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(name))
    ?.slice(name.length)
  return value === undefined ? true : value === 'true'
}

function writeSidebarCookie(userId: string | number | null | undefined, expanded: boolean): void {
  if (typeof document === 'undefined') return

  const name = encodeURIComponent(sidebarCookieName(userId))
  document.cookie = `${name}=${expanded}; Path=/; Max-Age=31536000; SameSite=Lax`
}

export default function DashboardLayout({ children, variant = 'default' }: Props) {
  const { props: pageProps, url } = usePage<PageProps>();
  const auth = pageProps.auth;
  const featureFlags = pageProps.featureFlags;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(() => readSidebarCookie(auth.user?.id));
  const [businessProcessChatRailOpen, setBusinessProcessChatRailOpen] = useState(false)
  const orgDropdown = useDropdown();
  const orgPanelRef = useRef<HTMLDivElement>(null);
  const dashboardPath = url.split('?')[0];
  const onWorkerChatPage = /^\/dashboard\/digital_workers\/[^/]+\/chat$/.test(dashboardPath);
  const onLegacyChatPage = dashboardPath === '/dashboard/agent_chats' ||
    dashboardPath.startsWith('/dashboard/agent_chats/');
  const onWorkerMemoryPage = /^\/dashboard\/digital_workers\/[^/]+\/memoria$/.test(dashboardPath);
  const onBusinessProcessCreatePage = dashboardPath === '/dashboard/business_processes/new';
  const onWideWorkerPage = onWorkerChatPage || onWorkerMemoryPage || onBusinessProcessCreatePage;
  // Painel/Chat sidebar mode (SZ-1504). Derived from the URL: the agent chat
  // page is the only Chat-mode destination, so being on /dashboard/agent_chats
  // IS Chat. This makes deep links and back/forward Just Work.
  const mode: 'painel' | 'chat' = url.startsWith('/dashboard/agent_chats') ? 'chat' : 'painel';

  const { theme, setTheme } = useTheme()
  // Cycles light -> dark -> system (SZ-1582); the row shows the current theme.
  const nextTheme = { light: 'dark', dark: 'system', system: 'light' } as const
  const themeIcons = { light: Sun, dark: Moon, system: Monitor }
  const themeLabels = { light: 'Tema: Claro', dark: 'Tema: Escuro', system: 'Tema: Sistema' }
  const ThemeIcon = themeIcons[theme]
  useProvisioningPoll()

  // The process editor is a fixed viewport rail. Reserve its width at the
  // dashboard-shell level so the canonical content column is not compressed
  // from inside its own max-width container.
  useEffect(() => subscribeBusinessProcessEditChatVisibility(({ open }) => {
    setBusinessProcessChatRailOpen(open)
  }), [])

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleExpanded = () => {
    setIsExpanded((prev) => {
      const newValue = !prev;
      writeSidebarCookie(auth.user?.id, newValue);
      return newValue;
    });
  };

  // Toggle navigates: Chat -> the agent chat page (opens the empty "escolha um
  // agente" state), Painel -> the agents page. The URL then drives `mode`.
  const goToMode = (target: 'painel' | 'chat') => {
    setIsMobileMenuOpen(false);
    if (target === mode) return;
    router.visit(target === 'chat' ? '/dashboard/agent_chats' : '/dashboard');
  };

  const shouldShowExpanded = isExpanded;
  const sidebarWidth = shouldShowExpanded ? 'w-52' : isMobileMenuOpen ? 'w-52 md:w-16' : 'md:w-16';

  // Track previous sidebar state
  const prevExpandedRef = useRef(shouldShowExpanded);
  const prevMobileMenuRef = useRef(isMobileMenuOpen);

  // Close dropdown when sidebar collapses (only on transition, not on initial render)
  useEffect(() => {
    const wasExpanded = prevExpandedRef.current || prevMobileMenuRef.current;
    const isNowCollapsed = !shouldShowExpanded && !isMobileMenuOpen;

    // Only close if sidebar transitioned from expanded to collapsed
    if (wasExpanded && isNowCollapsed) {
      orgDropdown.close();
    }

    prevExpandedRef.current = shouldShowExpanded;
    prevMobileMenuRef.current = isMobileMenuOpen;
  }, [shouldShowExpanded, isMobileMenuOpen, orgDropdown.close]);

  // Focus first interactive element when panel opens
  useEffect(() => {
    if (orgDropdown.isOpen && orgPanelRef.current) {
      const firstFocusable = orgPanelRef.current.querySelector<HTMLElement>(
        'button, [href], input'
      );
      firstFocusable?.focus();
    }
  }, [orgDropdown.isOpen]);

  // Lock body scroll while mobile sidebar is open so the page does not scroll behind the backdrop
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  // The preference belongs to the authenticated account. Switching accounts
  // in the same browser must not reuse another person's navigation state.
  useEffect(() => {
    setIsExpanded(readSidebarCookie(auth.user?.id));
  }, [auth.user?.id]);

  const mainContent = (
    <>
    <CommandPalette />
    {/* text-sm no wrapper (SZ-1587): corpo 14px só no shell do dashboard, por
        herança — marketing/auth/checkout ficam no 16px default. Tamanhos
        explícitos (text-xs, text-2xl…) não são afetados por serem rem-based. */}
    <div className={dashboardViewportClassName(variant)}>
      {/* Mobile Header - Always show */}
      <div className="md:hidden shrink-0 bg-background p-4 flex items-center justify-between border-b border-border">
        <img src="/landing/images/sz-orb-logo.png" alt="StationZero" className="h-5 object-contain dark:hidden" />
        <img src="/landing/images/sz-orb-logo-white.png" alt="StationZero" className="h-5 object-contain hidden dark:block" />
        {!isMobileMenuOpen && (
          <button
            onClick={toggleMobileMenu}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-accent-foreground hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring"
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          </button>
        )}
      </div>

      {/* Sidebar - Always show */}
      <div
        className={`
          ${isMobileMenuOpen ? 'flex fixed' : 'hidden'}
          md:flex md:flex-shrink-0
          flex-col inset-y-0 left-0 h-full md:h-auto
          bg-sidebar border-r border-border
          z-50 transition-all duration-300 ease-in-out
          ${sidebarWidth}
        `}
      >
        {/* Mobile Sidebar Header */}
        <div className="md:hidden p-4 border-b border-border">
          <img src="/landing/images/sz-orb-logo.png" alt="StationZero" className="h-5 object-contain dark:hidden" />
          <img src="/landing/images/sz-orb-logo-white.png" alt="StationZero" className="h-5 object-contain hidden dark:block" />
        </div>

        {/* Desktop Header */}
        <div className={`hidden md:flex items-center transition-all duration-300 py-3 ${
          shouldShowExpanded ? 'px-4 md:px-6' : 'justify-center'
        }`}>
          {/* StationZero branding - NOT clickable */}
          {shouldShowExpanded ? (
            <>
              <img src="/landing/images/sz-orb-logo.png" alt="StationZero" className="h-5 object-contain dark:hidden" />
              <img src="/landing/images/sz-orb-logo-white.png" alt="StationZero" className="h-5 object-contain hidden dark:block" />
            </>
          ) : (
            <LavaOrb />
          )}
        </div>

        {/* Main navigation */}
        <div className="flex-1 relative overflow-hidden flex flex-col">
          {/* Painel / Chat mode toggle */}
          <div className="px-2 pt-2 pb-1">
            {shouldShowExpanded || isMobileMenuOpen ? (
              <div role="group" aria-label="Modo da barra lateral" className="flex w-full gap-[2px] rounded-lg bg-muted p-1">
                <button
                  type="button"
                  onClick={() => goToMode('painel')}
                  aria-pressed={mode === 'painel'}
                  data-testid="sidebar-mode-painel"
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium transition-colors ${
                    mode === 'painel' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <LayoutDashboard className="h-4 w-4" strokeWidth={1.75} />
                  Painel
                </button>
                <button
                  type="button"
                  onClick={() => goToMode('chat')}
                  aria-pressed={mode === 'chat'}
                  data-testid="sidebar-mode-chat"
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium transition-colors ${
                    mode === 'chat' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <MessagesSquare className="h-4 w-4" strokeWidth={1.75} />
                  Chat
                </button>
              </div>
            ) : (
              <div role="group" aria-label="Modo da barra lateral" className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => goToMode('painel')}
                  className={`flex w-full items-center justify-center rounded-md py-1.5 ${
                    mode === 'painel' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent'
                  }`}
                  title="Painel"
                  aria-label="Painel"
                  data-testid="sidebar-mode-painel"
                >
                  <LayoutDashboard className="h-4 w-4" strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  onClick={() => goToMode('chat')}
                  className={`flex w-full items-center justify-center rounded-md py-1.5 ${
                    mode === 'chat' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent'
                  }`}
                  title="Chat"
                  aria-label="Chat"
                  data-testid="sidebar-mode-chat"
                >
                  <MessagesSquare className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </div>
            )}
          </div>

          {mode === 'painel' ? (
          <nav className="py-1">
            <ul className="space-y-1">
              {/* Agentes */}
              <NavItem
                href="/dashboard"
                icon={<Users className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />}
                label="Agentes"
                onClick={() => setIsMobileMenuOpen(false)}
                shouldShowExpanded={shouldShowExpanded}
                isMobileMenuOpen={isMobileMenuOpen}
              />

              <NavItem
                href="/dashboard/integrations"
                alsoMatchPaths={['/dashboard/composio', '/dashboard/credentials']}
                icon={<Cable className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />}
                label="Integrações"
                onClick={() => setIsMobileMenuOpen(false)}
                shouldShowExpanded={shouldShowExpanded}
                isMobileMenuOpen={isMobileMenuOpen}
              />

              {featureFlags?.business_processes_enabled && (
                <NavItem
                  href="/dashboard/business_processes"
                  icon={<Workflow className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />}
                  label="Processos"
                  onClick={() => setIsMobileMenuOpen(false)}
                  shouldShowExpanded={shouldShowExpanded}
                  isMobileMenuOpen={isMobileMenuOpen}
                />
              )}

              {pageProps.permissions?.can_access_forge === true && <LegacyForgeNavigation
                onNavigate={() => setIsMobileMenuOpen(false)} expanded={shouldShowExpanded} mobile={isMobileMenuOpen} />}
              {pageProps.permissions?.can_access_forge !== true && <li className="mx-2">
                <DashboardCatalogLink onNavigate={() => setIsMobileMenuOpen(false)} compact={!shouldShowExpanded && !isMobileMenuOpen} />
              </li>}

              {/* Artifacts */}
              <NavItem
                href="/dashboard/artifacts"
                icon={<FileCode2 className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />}
                label="Publicações"
                onClick={() => setIsMobileMenuOpen(false)}
                shouldShowExpanded={shouldShowExpanded}
                isMobileMenuOpen={isMobileMenuOpen}
              />

              <NavItem
                href="/dashboard/audit_logs"
                icon={<Fingerprint className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />}
                label="Auditoria"
                onClick={() => setIsMobileMenuOpen(false)}
                shouldShowExpanded={shouldShowExpanded}
                isMobileMenuOpen={isMobileMenuOpen}
              />

            </ul>
          </nav>
          ) : (
            <div className="min-h-0 flex-1">
              {(shouldShowExpanded || isMobileMenuOpen) && (
                <SidebarConversationsPanel onNavigate={() => setIsMobileMenuOpen(false)} />
              )}
            </div>
          )}

          {/* Inline org menu panel — anchored to bottom of nav area */}
          <div
            ref={orgPanelRef}
            data-dropdown
            className={`absolute bottom-0 left-0 right-0 bg-sidebar flex flex-col z-10 max-h-[80%] transition-opacity duration-200 ease-in-out ${
              orgDropdown.isOpen && (shouldShowExpanded || isMobileMenuOpen)
                ? 'opacity-100'
                : 'opacity-0 pointer-events-none'
            }`}
            aria-hidden={!orgDropdown.isOpen}
          >
            <div className="overflow-y-auto py-2 border-t border-border">
              {/* User Email */}
              {auth.user?.email_address && (
                <div className="px-4 py-2">
                  <span className="text-xs text-muted-foreground block truncate">
                    {auth.user.email_address}
                  </span>
                </div>
              )}

              {/* Organization Switcher */}
              <div>
                <div className="px-4 py-2">
                  <span className="text-xs text-muted-foreground font-medium">
                    Organizações
                  </span>
                </div>
                <OrganizationSwitcher onClose={orgDropdown.close} isOpen={orgDropdown.isOpen} />
              </div>

              {/* Configurações */}
              <div className="border-t border-border mt-2 pt-2">
                <Link
                  href={`/dashboard/organizations/${auth.user?.active_organization?.slug || ''}`}
                  className="flex items-center px-4 py-2 text-foreground hover:bg-accent transition-colors"
                  onClick={orgDropdown.close}
                >
                  <Settings className="h-4 w-4 mr-3" strokeWidth={1.75} />
                  <span>Configurações</span>
                </Link>

                {/* Admin - Command Center */}
                {auth.user?.is_admin && (
                  <Link
                    href="/cc"
                    className="flex items-center px-4 py-2 text-foreground hover:bg-accent transition-colors"
                    onClick={orgDropdown.close}
                  >
                    <Shield className="h-4 w-4 mr-3" strokeWidth={1.75} />
                    <span>Admin</span>
                  </Link>
                )}

                {/* Theme Toggle */}
                <button
                  onClick={() => setTheme(nextTheme[theme])}
                  title="Mudar tema"
                  className="flex items-center px-4 py-2 text-foreground hover:bg-accent transition-colors w-full"
                >
                  <ThemeIcon className="h-4 w-4 mr-3" strokeWidth={1.75} />
                  <span>{themeLabels[theme]}</span>
                </button>
              </div>

              {/* Sair */}
              <div className="border-t border-border mt-2 pt-2">
                <button
                  onClick={() => {
                    orgDropdown.close();
                    if (confirm('Tem certeza que deseja sair?')) {
                      router.delete('/session', {
                        onSuccess: () => setIsMobileMenuOpen(false),
                      });
                    }
                  }}
                  className="flex items-center px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors w-full"
                >
                  <LogOut className="h-4 w-4 mr-3" strokeWidth={1.75} />
                  <span>Sair</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {auth.user?.is_admin && <Link href="/cc/mcp_accesses" onClick={() => setIsMobileMenuOpen(false)}
          aria-label={pageProps.navigationLabels.local_agents} title={pageProps.navigationLabels.local_agents}
          data-testid="legacy-mcp-link" className={`flex shrink-0 items-center gap-3 border-t border-border py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground ${shouldShowExpanded || isMobileMenuOpen ? 'px-4' : 'justify-center'}`}>
          <Plug className="size-4 shrink-0" strokeWidth={1.75} />{(shouldShowExpanded || isMobileMenuOpen) && <span>{pageProps.navigationLabels.local_agents}</span>}
        </Link>}

        {/* Bottom - Organization Switcher Toggle */}
        <div className="border-t border-border mt-auto">
          <button
            ref={orgDropdown.buttonRef}
            onClick={() => {
              if (!shouldShowExpanded && !isMobileMenuOpen) {
                setIsExpanded(true);
                writeSidebarCookie(auth.user?.id, true);
                setTimeout(() => orgDropdown.toggle(), 300);
              } else {
                orgDropdown.toggle();
              }
            }}
            className={`w-full flex items-center gap-3 py-3 hover:bg-accent transition-colors ${
              shouldShowExpanded || isMobileMenuOpen ? 'px-4' : 'justify-center'
            }`}
            aria-expanded={orgDropdown.isOpen}
            aria-label="Menu da organização"
          >
            <Avatar
              name={auth.user?.active_organization?.name || 'O'}
              size="sm"
              className="rounded-lg flex-shrink-0"
            />
            {(shouldShowExpanded || isMobileMenuOpen) && (
              <>
                <span className="flex-1 font-medium text-foreground text-left truncate">
                  {auth.user?.active_organization?.name || 'Organização'}
                </span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                  orgDropdown.isOpen ? 'rotate-180' : ''
                }`} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div
        data-content-rail-state={businessProcessChatRailOpen ? 'open' : 'closed'}
        className={`flex min-h-0 min-w-0 flex-1 flex-col transition-[margin] duration-200 ease-out motion-reduce:transition-none ${
        businessProcessChatRailOpen ? 'lg:mr-[480px]' : ''
      } ${
        dashboardContentOverflowClassName(variant, onWorkerChatPage || onLegacyChatPage || onWorkerMemoryPage || onBusinessProcessCreatePage)
      }`}
      >
        {/* Sidebar toggle remains available in both modes; in Chat it controls
            the conversation history rail and leaves the thread full-width. */}
        <div className="hidden md:flex items-center h-10 px-3 flex-shrink-0">
          <button
            onClick={toggleExpanded}
            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-accent-foreground hover:bg-accent transition-colors"
            title={shouldShowExpanded ? 'Recolher sidebar' : 'Expandir sidebar'}
            aria-label={shouldShowExpanded ? 'Recolher sidebar' : 'Expandir sidebar'}
            data-testid="dashboard-sidebar-toggle"
          >
            <PanelLeft className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
        <FlashMessages />
        <UndoToastHost />

        {/* Content area - conditional padding based on variant */}
        {variant !== 'default' ? (
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
            {children}
          </div>
        ) : (
          <div
            className={`flex min-w-0 flex-1 flex-col ${onWorkerChatPage || onBusinessProcessCreatePage ? 'p-0' : 'p-4 md:p-8'} ${
              onWideWorkerPage ? 'min-h-0' : ''
            }`}
          >
            {/* Chat and memory grow with the viewport; other dashboard pages
                keep the canonical portal width. */}
            <div
              data-dashboard-content-width={onWideWorkerPage ? 'wide' : 'standard'}
              className={`min-w-0 flex-1 w-full mx-auto transition-[max-width] duration-500 ease-out motion-reduce:transition-none ${
                onWideWorkerPage ? 'flex min-h-0 flex-col' : ''
              } ${onWideWorkerPage ? 'max-w-[100%]' : 'max-w-5xl'}`}
            >
              {children}
            </div>
          </div>
        )}
      </div>

      {/* Mobile menu backdrop overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/20 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

    </div>
    </>
  );

  // Worker-first navigation is the authenticated default (SZ-1558); the
  // legacy shell remains as a fallback. Keep the widget outside that choice so
  // one persistent instance serves every user-dashboard page.
  const dashboardShell = featureFlags?.worker_first_nav_enabled
    ? (
      <WorkerFirstSidebar variant={variant} contentRailOpen={businessProcessChatRailOpen}>
        {children}
      </WorkerFirstSidebar>
    )
    : mainContent;

  return (
    <>
      {dashboardShell}
      {!onLegacyChatPage && (
        <AssistantFloatingWidgetHost hidden={onWorkerChatPage || onBusinessProcessCreatePage} />
      )}
    </>
  );
}
